/**
 * W-9 Service
 * Following the same clean architecture pattern as W8BEN and W8ECI services
 */

import { W9FormModel, W9ResponseModel, W9FieldMapper } from "../Models/W9Models";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { updateFileData } from "./fileDataService";

export class W9Service {
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  /**
   * Create form model from API response data
   */
  static fromApiResponse(apiData: any): W9FormModel {
    // Create response model from API data
    const responseModel = new W9ResponseModel(apiData);

    // Map legacy API field names
    this.mapLegacyApiFields(apiData, responseModel);

    // Use field mapper to convert to form model
    const formModel = W9FieldMapper.mapResponseToForm(responseModel);

    // Always set needToBeSigned to 'yes' for W9 forms
    formModel.needToBeSigned = "yes";

    return formModel;
  }

  /**
   * Map legacy API field names to current field names
   */
  private static mapLegacyApiFields(apiData: any, responseModel: W9ResponseModel): void {
    const legacyMappings: Record<string, string[]> = {
      "Name of organization": [
        "Organization_name",
        "organization_name",
        "Name",
        "name",
        "entity_name",
      ],
      "First name and middle name": ["FirstName", "first_name", "First_name_and_middle_name"],
      "Last name": ["LastName", "last_name", "Last_name"],
      "W9-Business_name": ["Business_name", "business_name", "W9-Business_name"],
      "Permanent residence address": [
        "Permanent_residence_address",
        "permanent_address",
        "address_line_1",
      ],
      "Permanent city address": ["Permanent_city_address", "permanent_city", "city_state_zip"],
      "Permanent address country": ["Permanent_address_country", "permanent_country", "country"],
      "Social security number": ["SSN", "ssn", "Social_security_number"],
      "Employer identification number": ["EIN", "ein", "Employer_identification_number"],
      Signature_date: ["Signature_date", "signature_date", "sign_date"],
      Print_name: ["Print_name", "printed_name", "signer_name", "Signer_printed_name"],
      Signature: ["Signature", "signature"],

      isSigned: ["isSigned", "is_signed", "signed"],
      isModified: ["isModified", "is_modified", "modified"],
      Form_number: ["Form_number", "form_number", "form_name"],
      Form_revision_date: ["Form_revision_date", "form_revision", "revision_date"],

      // Tax classification fields
      "W9-Tax_classification_individual_sole_proprietor": [
        "W9-Tax_classification_individual_sole_proprietor",
        "individual",
        "sole_proprietor",
      ],
      "W9-Tax_classification_c_corporation": [
        "W9-Tax_classification_c_corporation",
        "c_corp",
        "c_corporation",
      ],
      "W9-Tax_classification_s_corporation": [
        "W9-Tax_classification_s_corporation",
        "s_corp",
        "s_corporation",
      ],
      "W9-Tax_classification_partnership": ["W9-Tax_classification_partnership", "partnership"],
      "W9-Tax_classification_trust_estate": [
        "W9-Tax_classification_trust_estate",
        "trust",
        "estate",
      ],
      "W9-Tax_classification_llc": ["W9-Tax_classification_llc", "llc"],
      "W9-Tax_classification_other": ["W9-Tax_classification_other", "other_entity"],

      // Other W9 specific fields
      "W9-3B_foreign_partners": [
        "W9-3B_foreign_partners",
        "foreign_partners",
        "W9_3B_foreign_partners",
        "W9-3B-foreign_partners",
        "foreign_partners_owners_beneficiaries",
        "partnership_trust_estate_foreign_partners",
        "W9-foreign_partners",
        "foreign_partner_question",
        "W9-Line3B-foreign_partners",
      ],
      "W9-Exempt_payee_code": ["W9-Exempt_payee_code", "exempt_payee", "payee_code"],
      "W9-Fatca_exemption_code": ["W9-Fatca_exemption_code", "fatca_exemption", "fatca_code"],
      "W9-Requester_name_address": ["W9-Requester_name_address", "requester_info"],
      "W9-Account_numbers": [
        "W9-Account_numbers",
        "account_numbers",
        "W9_Account_numbers",
        "W9-account_numbers",
        "account_number_list",
        "account_numbers_list",
        "W9-account_number_list",
        "requester_account_numbers",
      ],
      Chapter4Status: ["Chapter4Status", "chapter_4_status"],
    };

    Object.entries(legacyMappings).forEach(([modelField, legacyFields]) => {
      if (!responseModel[modelField]) {
        for (const legacyField of legacyFields) {
          if (apiData[legacyField] !== undefined) {
            responseModel[modelField] = apiData[legacyField];
            break;
          }
        }
      }
    });

    // Handle address components if we have them separately
    if (!responseModel["Permanent city address"]) {
      const city = apiData["Permanent_address_city"] || apiData["permanent_city"] || "";
      const state =
        apiData["Permanent_address_state_NonUS"] ||
        apiData["Permanent_address_state_USCA"] ||
        apiData["permanent_state"] ||
        "";
      const zip = apiData["Permanent_address_zip"] || apiData["permanent_zip"] || "";
      if (city || state || zip) {
        responseModel["Permanent city address"] = `${city}, ${state}, ${zip}`
          .replace(/,\s*,/g, ",")
          .replace(/^,\s*|,\s*$/g, "");
      }
    }
  }

  /**
   * Convert form model to API payload format
   */
  static toApiPayload(model: W9FormModel): Record<string, any> {
    const payload: Record<string, any> = {};

    // Map all model fields to API payload
    Object.keys(model).forEach((field) => {
      const value = (model as any)[field];
      if (value !== undefined && typeof value !== "function") {
        payload[field] = value;
      }
    });

    return payload;
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: W9FormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      // Enhanced authentication with multiple fallbacks
      let userData;
      let token;

      try {
        userData = getUserIdFromLocalStorage();
        token = userData?.token;
      } catch (error) {
        // console.log("W9 Auth - getUserIdFromLocalStorage failed:", error);
      }

      // Fallback authentication methods
      if (!token) {
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            token = parsed.token;
          }
        } catch (error) {
          console.log("W9 Auth - fallback userData parsing failed:", error);
        }
      }

      if (!token) {
        try {
          token = localStorage.getItem("token");
        } catch (error) {
          console.log("W9 Auth - direct token retrieval failed:", error);
        }
      }

      if (!token) {
        console.error("W9 Auth - No authentication token found after all fallbacks");
        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = this.toApiPayload(model);

      formData["Entity Type"] = "";
      formData["Form_number"] = "";

      let formsDataArray;
      try {
        // The formsData could be a complex structure or simple array
        const parsedData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedData &&
          typeof parsedData === "object" &&
          parsedData.formsData &&
          Array.isArray(parsedData.formsData)
        ) {
          // Find and update the W9 form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedData.formsData.length; i++) {
            const formItem = parsedData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (dataItem && (dataItem.formType === "W9" || dataItem.Form_number === "W-9")) {
                  parsedData.formsData[i].data[j] = {
                    ...dataItem,
                    ...formData,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            parsedData.formsData.push({
              request_id: "new-w9-" + Date.now(),
              status: "success",
              data: [formData],
              reportdata: {
                requestId: "new-w9-" + Date.now(),
                subRequestId: "new-w9-" + Date.now(),
                taxForms: "W9",
                type: "extraction",
              },
            });
          }

          parsedData["Permanent city address"] = "";
          row.formsData = JSON.stringify(parsedData);
        } else {
          // Handle as simple array
          parsedData["Permanent city address"] = "";
          formsDataArray = Array.isArray(parsedData) ? parsedData : [];

          const currentItem = formsDataArray[selectedIndex];

          if (selectedIndex < formsDataArray.length && currentItem) {
            formsDataArray[selectedIndex] = {
              ...currentItem,
              ...formData,
            };
          } else {
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            formsDataArray[selectedIndex] = formData;
          }

          row.formsData = JSON.stringify(formsDataArray);
        }
      } catch (error) {
        console.log("Error parsing existing formsData, creating new array:", error);
        formsDataArray = [formData];
        row.formsData = JSON.stringify(formsDataArray);
      }

      // Send to API using centralized service
      await updateFileData(row);

      return true;
    } catch (error) {
      console.error("Failed to save W-9 form:", error);
      throw error;
    }
  }
}
